import { client } from './index';

export interface SupportAccessSessionRow {
  id: number;
  actor_email: string;
  account_id: number;
  account_name: string | null;
  entered_at: string;
  returned_at: string | null;
}

export interface SupportAccessState {
  /** A support session is open in this browser session. */
  inside: boolean;
  /** The customer account being supported, while inside. */
  account_name: string | null;
  /** Where the return leads back to. */
  origin_account_name: string | null;
  sessions: SupportAccessSessionRow[];
}

export interface SupportAccessCandidate {
  id: number;
  name: string;
  /** Its `info+helpds<id>@` row exists, without which entering is refused. */
  has_support_user: boolean;
}

export interface SupportAccessEntered {
  account_id: number;
  account_name: string;
  origin_account_name: string | null;
}

async function getState(): Promise<SupportAccessState> {
  const { data } = await client.get<SupportAccessState>('/support_access');
  return data;
}

/** The accounts this person can enter, so getting in does not need an id by heart. */
async function searchAccounts(search: string): Promise<SupportAccessCandidate[]> {
  const { data } = await client.get<{ accounts: SupportAccessCandidate[] }>(
    '/support_access/accounts',
    { params: { search } },
  );
  return data.accounts;
}

/**
 * The target is derived server-side from the account: sending a user id would
 * turn the permission into "sign in as anyone".
 */
async function enter(accountId: number): Promise<SupportAccessEntered> {
  const { data } = await client.post<SupportAccessEntered>('/support_access', {
    account_id: accountId,
  });
  return data;
}

async function leave(): Promise<{ account_id: number; account_name: string | null }> {
  const { data } = await client.delete<{ account_id: number; account_name: string | null }>(
    '/support_access',
  );
  return data;
}

export const supportAccessApi = { getState, searchAccounts, enter, leave };
